import { useCallback, useEffect, useState } from "react";
import { Check, KeyRound, LogOut, UserPlus, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { api, errorMessage } from "../lib/api";
import { RegistrationForm } from "../components/opportunities/RegistrationForm";
import { Reveal } from "../components/Reveal";

const fmt = (iso) => new Date(iso).toLocaleDateString("ar-SA", { dateStyle: "medium" });
const money = (n) => `${Number(n || 0).toLocaleString("en-US")} ر.س`;

const Gate = ({ onLogin }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("login");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    const code = new FormData(e.currentTarget).get("code").trim().toUpperCase();
    try { await api.get("/coordinators/me", { headers: { "X-Coordinator-Code": code } }); onLogin(code); }
    catch (err) { setError(errorMessage(err, t.coord.invalid)); }
    finally { setLoading(false); }
  };
  const register = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/coordinators/register", Object.fromEntries(new FormData(e.currentTarget).entries())); setSent(true); }
    catch (err) { toast.error(errorMessage(err, t.reg.error)); }
    finally { setLoading(false); }
  };

  return (
    <main className="container" data-testid="coordinator-gate">
      <Reveal className="page-head"><span className="kicker">Partners</span><h1>{t.coord.title}</h1><p>{t.coord.sub}</p></Reveal>
      <div className="panel" style={{ maxWidth: 560, marginBottom: 80 }}>
        <div className="tabs"><button className={`tab ${tab === "login" ? "active" : ""}`} onClick={() => setTab("login")} data-testid="coord-tab-login">{t.coord.loginTab}</button><button className={`tab ${tab === "register" ? "active" : ""}`} onClick={() => setTab("register")} data-testid="coord-tab-register">{t.coord.registerTab}</button></div>
        {tab === "login" ? (
          <form className="form" onSubmit={login} style={{ gridTemplateColumns: "1fr" }}>
            <label className="full">{t.coord.code}<input name="code" placeholder="CO-XXXXXX" required className="en" style={{ letterSpacing: ".12em", textTransform: "uppercase" }} data-testid="coord-code-input" /></label>
            {error && <div className="error-text" data-testid="coord-login-error">{error}</div>}
            <button className="btn btn-primary full" disabled={loading} data-testid="coord-login-button">{loading ? <span className="loader" /> : <><KeyRound size={16} />{t.coord.enter}</>}</button>
          </form>
        ) : sent ? (
          <div className="success-box" data-testid="coord-register-success"><span className="ring"><Check size={30} /></span><h3>{t.coord.sentTitle}</h3><p className="muted" style={{ marginTop: 8 }}>{t.coord.sentSub}</p></div>
        ) : (
          <form className="form" onSubmit={register} style={{ gridTemplateColumns: "1fr" }}>
            <label>{t.coord.name}<input name="name" required data-testid="coord-name-input" /></label>
            <label>{t.coord.email}<input name="email" type="email" required data-testid="coord-email-input" /></label>
            <label>{t.coord.phone}<input name="phone" type="tel" required placeholder="+966 5XXXXXXXX" data-testid="coord-phone-input" /></label>
            <button className="btn btn-primary" disabled={loading} data-testid="coord-register-button">{loading ? <span className="loader" /> : t.coord.send}</button>
          </form>
        )}
      </div>
    </main>
  );
};

const Portal = ({ code, onLogout }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(null);
  const headers = { "X-Coordinator-Code": code };
  const load = useCallback(() => api.get("/coordinators/me", { headers }).then((r) => setData(r.data)).catch(onLogout), [code]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  if (!data) return <main className="page-loader"><span className="loader" /></main>;

  const { coordinator: c, students, opportunities, finance } = data;
  const groups = Object.values(students.reduce((acc, s) => { (acc[s.opportunity_id] ||= { title: s.opportunity_title, spec: s.specialty_label, stage: s.opportunity_stage, items: [] }).items.push(s); return acc; }, {}));

  return (
    <main className="container" data-testid="coordinator-portal">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div><span className="kicker">{t.coord.title}</span><h1 style={{ fontSize: "clamp(1.4rem,3vw,2rem)" }}>{t.coord.welcome}، {c.name}</h1><p className="en" style={{ marginTop: 6 }}>{c.access_code}</p></div>
        <button className="btn btn-outline btn-sm" onClick={onLogout} data-testid="coord-logout"><LogOut size={15} />{t.coord.logout}</button>
      </div>
      <div className="portal-grid" style={{ marginBottom: 80 }}>
        <div>
          <div className="panel"><h3><UserPlus size={20} />{t.coord.opps}</h3>
            {opportunities.length === 0 ? <p className="muted">{t.opps.empty}</p> : <div className="table-wrap" style={{ marginTop: 14, boxShadow: "none" }}><table className="tbl" style={{ minWidth: 0 }}><tbody>
              {opportunities.map((o) => <tr key={o.id} data-testid={`coord-opp-${o.id}`}><td><b>{o.specialty_ar}</b><br /><small className="en muted">{o.title_en}</small></td><td style={{ whiteSpace: "nowrap" }}>{o.remaining_seats} / {o.total_seats}</td><td><button className="mini-btn primary" onClick={() => setAdding(o)} data-testid={`coord-add-${o.id}`}><UserPlus size={14} />{t.coord.addStudent}</button></td></tr>)}
            </tbody></table></div>}
          </div>
          <div className="panel" style={{ marginTop: 16 }}><h3>{t.coord.students} <span className="badge badge-green">{students.length}</span></h3>
            {groups.length === 0 ? <p className="muted">{t.coord.none}</p> : groups.map((g) => (
              <div key={g.title}><div className="group-head"><div><span className="badge badge-indigo">{g.spec}</span> <span className="en">{g.title}</span></div><div style={{ display: "flex", gap: 6 }}><span className="badge badge-muted">{g.items.length} {t.coord.count}</span>{g.stage && <span className="badge badge-gold">{t.coord.oppStage}: {g.stage}</span>}</div></div>
                <div className="table-wrap" style={{ boxShadow: "none" }}><table className="tbl" style={{ minWidth: 560 }}><thead><tr><th>{t.coord.name}</th><th>{t.reg.phone.split("—")[1] || "Phone"}</th><th>{t.coord.fee}</th><th>الحالة</th><th>التاريخ</th></tr></thead><tbody>
                  {g.items.map((s) => <tr key={s.id}><td><b>{s.full_name}</b><br /><small className="muted">{s.specialty}</small></td><td className="en">{s.phone}</td><td>{money(s.fee)}</td><td><span className="badge badge-green">{s.status}</span></td><td style={{ fontSize: 12 }}>{fmt(s.created_at)}</td></tr>)}
                </tbody></table></div></div>))}
          </div>
        </div>
        <aside><div className="panel" data-testid="coord-finance"><h3><Wallet size={20} />{t.coord.finance}</h3>
          <div className="finance"><div><small>{t.coord.due}</small><b>{money(finance.total_due)}</b></div><div><small>{t.coord.paid}</small><b className="ok">{money(finance.total_paid)}</b></div><div><small>{t.coord.remaining}</small><b className={finance.remaining > 0 ? "warn" : "ok"}>{money(finance.remaining)}</b></div></div>
          <h4 style={{ fontSize: 13, marginTop: 18, color: "var(--muted)" }}>{t.coord.payments}</h4>
          {finance.payments.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>{t.coord.noPayments}</p> : finance.payments.map((p) => <div className="detail-row" key={p.id}><small>{fmt(p.at)}</small><span><b>{money(p.amount)}</b> {p.note && <span className="muted">— {p.note}</span>}</span></div>)}
        </div></aside>
      </div>
      {adding && <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setAdding(null)}><div className="modal" data-testid="coord-student-modal">
        <button className="modal-close" onClick={() => setAdding(null)}><X size={18} /></button>
        <h2>{t.coord.addStudent}<span className="en">{adding.title_en}</span></h2>
        <RegistrationForm opp={adding} endpoint="/coordinators/students" headers={headers} compact onDone={() => { load(); setTimeout(() => setAdding(null), 1800); }} />
      </div></div>}
    </main>
  );
};

export default function CoordinatorPage() {
  const [code, setCode] = useState(() => localStorage.getItem("rn_coord") || "");
  const login = (c) => { localStorage.setItem("rn_coord", c); setCode(c); };
  const logout = () => { localStorage.removeItem("rn_coord"); setCode(""); };
  return code ? <Portal code={code} onLogout={logout} /> : <Gate onLogin={login} />;
}
